<template>
    <div class="col-sm-12 px-1" style="border: 1px solid #BCBCBC; border-radius: .5em;">
        <div v-if="diaryData" class="d-flex flex-column" style="height: 42em">
            <!-- Header part -->
            <header class="flex-shrink-0 p-3 border-bottom">
                <div class="col-sm-12">
                    <div class="row">
                        <div class="col-10">
                            <h3 v-if="diaryData.id < 1" class="editor-header">{{ $t(titleKey) }}</h3>
                            <h4 v-else class="form-header">
                                {{ $t(titleKey) }}
                            </h4>
                        </div>
                        <div class="col-2 d-flex justify-content-end">
                            <font-awesome-icon
                                v-if="showEdit"
                                icon="edit"
                                class="print-icon ml-auto"
                                style="margin-right: .5em"
                                @click.stop="$emit('edit')"
                            />
                            <font-awesome-icon
                                icon="print"
                                class="print-icon ml-auto"
                                @click.stop="downloadPdf"
                            />
                        </div>
                    </div>
                </div>
            </header>

            <!-- Content -->
            <main class="flex-grow-1 overflow-auto p-3">
                <div class="row">
                    <span class="item-details">
                        {{
                            mode === 'weekly' ? getDate(diaryData.record_date, true) + ' - ' + getDate(diaryData.record_end_date, true) : getDate(diaryData[dateField], true)
                        }}
                    </span>
                </div>
                <div class="row">
                    <span class="item-details">{{ diaryData.contract_name }}</span>
                </div>
                <div class="row pb-3">
                    <span class="item-details">{{ diaryData.contractor }}</span>
                </div>

                <div class="row">
                    <div class="col-sm-12">
                        <div class="row">
                            <h4 class="form-header pt-2 pb-1">{{ $t('contract_diary.supervisors') }}</h4>
                        </div>
                        <div class="row">
                            <span class="span-title">{{ diaryData.supervisors }}</span>
                        </div>
                    </div>
                </div>
                <div class="row">
                    <h4 class="form-header pt-2 pb-1">{{ $t('contract_diary.events') }}</h4>
                    <span class="span-title">{{ diaryData.events }}</span>
                </div>
                <div class="row">
                    <div class="col-sm-12">
                        <div class="row">
                            <h4 class="form-header pt-2 pb-1">{{ $t('contract_diary.deviations') }}</h4>
                        </div>
                        <div class="row">
                            <span class="span-title">{{ diaryData.deviations }}</span>
                        </div>
                    </div>
                </div>
                <div v-if="diaryData.photos && diaryData.photos.length > 0" class="mt-4 pl-1">
                    <!-- PHOTOS -->
                    <h4 class="form-header">{{ $t('area_report_details.photos') }}</h4>
                    <div v-if="diaryData.photos.length > 1" class="row text-center mt-2">
                        <div class="col-sm-4 pl-1 multi-img-row" v-for="(photo, index) in diaryData.photos"
                             v-bind:key="index">
                            <div class="image-container">
                                <ImageView
                                    :img-size="'sm'"
                                    :url="getPhotoUrl(mode === 'weekly' ? contractWeeklyDiaryPhotoUrl : contractDiaryPhotoUrl, mode === 'weekly' ? diaryData.id : photo.diary, mode === 'weekly' ? photo.id : photo.photo)"
                                    :contain="true"
                                    :height="'300px'"
                                    :controlSize="'sm'"
                                />
                            </div>
                        </div>
                    </div>
                    <div v-else-if="diaryData.photos.length > 0" class="row text-center">
                        <div class="col-12">
                            <div class="image-container">
                                <ImageView
                                    :img-size="'sm'"
                                    :url="getPhotoUrl(mode === 'weekly' ? contractWeeklyDiaryPhotoUrl : contractDiaryPhotoUrl, mode === 'weekly' ? diaryData.id : diaryData.photos[0].diary, mode === 'weekly' ? diaryData.photos[0].id : diaryData.photos[0].photo)"
                                    :contain="true"
                                    :height="'300px'"
                                    :controlSize="'sm'"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <div class="col-12 mt-2">
                    <h4 class="form-header">{{ $t('city_contract_diary.compelted_tasks') }}</h4>
                    <table class="table table-sm">
                        <tbody>
                        <tr v-for="(item, index) in diaryData.tasks" v-bind:key="index">
                            <td>{{ item.task }}</td>
                            <td>{{ toLocalTime(item.start_time) }} - {{ toLocalTime(item.end_time) }}</td>
                            <td>{{ getWorkTime(Math.round(item.work_duration / 60)) }} {{
                                    $t('contracts.unit_hours')
                                }}
                            </td>
                            <td>{{ Number(item.work_distance).toFixed(2) }} {{ $t('contracts.unit_km') }}</td>
                        </tr>
                        </tbody>
                    </table>
                </div>
                <div class="col-12 mt-2">
                    <h4 class="form-header">{{ $t('city_contract_diary.resources') }}</h4>
                    <table class="table table-sm">
                        <tbody>
                        <tr class="space-cells" v-for="(item, index) in diaryData.resources" v-bind:key="index">
                            <td>{{
                                    item.resource === -1 ? $t('city_contract_diary.resources_persons') : getVehicleTypeName(item.resource)
                                }}
                            </td>
                            <td>{{ item.count }} {{ $t('common.pcs') }}</td>
                        </tr>
                        </tbody>
                    </table>
                </div>
                <div class="col-12 mt-2">
                    <div v-if="diaryData.materials && diaryData.materials.length > 10"
                         class="col-12 d-flex justify-content-between align-items-center pb-1">
                        <div class="flex-grow-1">
                            <h4 class="form-header pointer hover-header"
                                @click.stop="materialsVisible = !materialsVisible">
                                {{ $t('city_contract_diary.materials') }} ({{ diaryData.materials.length }})
                            </h4>
                        </div>
                        <div class="ms-2 px-2" @click.stop="materialsVisible = !materialsVisible">
                            <font-awesome-icon icon="arrow-down"
                                               class="pointer no-focus arrow-icon dont-print hover-header"
                                               :class="{ rotated: materialsVisible }"/>
                        </div>
                    </div>
                    <h4 v-else class="form-header">{{ $t('city_contract_diary.materials') }}</h4>
                    <div
                        :class="{ 'routa-collapse': diaryData.materials && diaryData.materials.length > 10, collapsed: materialsVisible }">
                        <table v-if="diaryData.materials && diaryData.materials.length > 0" class="table table-sm">
                            <tbody>
                            <tr class="space-cells" v-for="(item, index) in diaryData.materials" v-bind:key="index">
                                <td>{{ item.material }}</td>
                                <td>{{ item.amount }} {{ item.unit }}</td>
                            </tr>
                            </tbody>
                        </table>
                        <span v-else class="span-title">-</span>
                    </div>
                </div>
                <div class="col-12 mt-2">
                    <h4 class="form-header">{{ $t('city_contract_diary.danger_reports') }}</h4>
                    <table v-if="diaryData.danger_reports && diaryData.danger_reports.length > 0"
                           class="table table-sm">
                        <tbody>
                        <tr v-for="(item, index) in diaryData.danger_reports" v-bind:key="index">
                            <td>{{ item.incidence }}</td>
                            <td>{{ toLocalTime(item.time) }}</td>
                            <td>{{ item.description }}</td>
                        </tr>
                        </tbody>
                    </table>
                    <span v-else class="span-title">-</span>
                </div>
                <div class="col-12 mt-2">
                    <div v-if="diaryData.weather && diaryData.weather.length > 10"
                         class="col-12 d-flex justify-content-between align-items-center pb-1">
                        <div class="flex-grow-1">
                            <h4 class="form-header pointer hover-header" @click.stop="weatherVisible = !weatherVisible">
                                {{ $t('weather_station.data_title') }} ({{ diaryData.weather.length }})
                            </h4>
                        </div>
                        <div class="ms-2 px-2" @click.stop="weatherVisible = !weatherVisible">
                            <font-awesome-icon icon="arrow-down"
                                               class="pointer no-focus arrow-icon dont-print hover-header"
                                               :class="{ rotated: weatherVisible }"/>
                        </div>
                    </div>
                    <h4 v-else class="form-header">{{ $t('weather_station.data_title') }}</h4>
                    <div
                        :class="{ 'routa-collapse': diaryData.weather && diaryData.weather.length > 10, collapsed: weatherVisible }">
                        <table class="table table-sm">
                            <thead>
                            <tr>
                                <th>{{ $t('contracts.external.time') }}</th>
                                <th>{{ $t('contracts.external.station') }}</th>
                                <th>{{ $t('contracts.external.air_temp') }}</th>
                                <th>{{ $t('contracts.external.road_temp') }}</th>
                                <th>{{ $t('contracts.external.avg_wind') }}</th>
                                <th>{{ $t('contracts.external.rain_type') }}</th>
                                <th>{{ $t('contracts.external.rain_amount') }}</th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr v-for="(item, index) in diaryData.weather" v-bind:key="index">
                                <td>{{ toLocalTime(item.saatieto.havaintoaika) }}</td>
                                <td>{{ item.saatieto['aseman-tunniste'] }}</td>
                                <td>{{ item.saatieto['ilman-lampotila'] }}</td>
                                <td>{{ item.saatieto['tien-lampotila'] }}</td>
                                <td>{{ item.saatieto['keskituuli'] }}</td>
                                <td>{{ getRainTypeText(item.saatieto['sateen-olomuoto']) }}</td>
                                <td>{{ item.saatieto['sadesumma'] }}</td>
                            </tr>
                            </tbody>
                        </table>
                    </div>
                </div>


            </main>

            <!-- Footer -->
            <footer class="flex-shrink-0 p-3 border-top">
                <div class="col-sm-12 my2 d-flex justify-content-end">
                    <button class="btn btn-outline-primary form-button" @click.stop="$emit('edit')">
                        {{ $t('common.edit') }}
                    </button>
                    <button
                        v-if="apikey && data && !!contract.work_site_key"
                        class="btn btn-primary form-button" :disabled="!data" @click.stop="sendToBuildie">
                        {{ $t('city_contract_diary.send_to_integration_service') }}
                    </button>
                </div>
            </footer>
        </div>
    </div>
    <div
        v-if="loading"
        id="loader"
        class="spinner"
    ></div>
</template>

<script>
import {restApi} from "@/components/mixins/RestApiMixin";
import {timeUtils} from "@/components/mixins/TimeUtils";
import {vehicleHelper} from '@/components/mixins/VehicleMixin'
import {PdfGeneratorManager} from "@/modules/PdfGeneratorManager";
import ImageView from "@/components/view/ImageView";
import {userHelper} from "@/components/mixins/UserMixin";
import {weatherHelper} from "@/components/mixins/WeatherMixin";
import {inject} from "vue";
import {pdfMixin} from "@/components/mixins/PdfMixin";

export default {
    name: 'CityContractDiary',
    components: {ImageView},
    mixins: [timeUtils, vehicleHelper, restApi, userHelper, weatherHelper, pdfMixin],
    props: {
        mode: {
            type: String,
            default: 'city',
            validator: value => ['city', 'weekly'].includes(value)
        },
        diary: {
            type: Object,
            default: null
        },
        contract: {
            type: Object,
            default: null
        },
        date: {
            type: Date,
            default: new Date()
        },
        user: {
            type: Object,
            default: null
        },
        showEdit: {
            type: Boolean,
            default: true
        }
    },
    emits: ['close'],
    setup() {
        // Inject parents alerter
        const showAlert = inject('showAlert');
        return {showAlert};
    },
    data: function () {
        return {
            loading: false,
            companyLogo: null,
            apikey: false,
            data: null,
            weatherVisible: false,
            materialsVisible: false
        }
    },
    watch: {
        contract() {
            this.fetchApiKey()
        },
        date() {
            this.fetchDiary()
        }
    },
    computed: {
        diaryData() {
            return this.mode === 'weekly' ? this.diary : this.data
        },
        dateField() {
            return this.mode === 'weekly' ? 'record_date' : 'date'
        },
        titleKey() {
            return this.mode === 'weekly' ? 'contract_diary.weekly_diary_title' : 'city_contract_diary.city_contract_diary_title'
        },
        isDateToday() {
            if (!this.date) {
                return true
            }
            const today = new Date()
            return (today.getFullYear() === this.date.getFullYear() && today.getMonth() === this.date.getMonth() && today.getDate() === this.date.getDate())
        },
        isContractSelected() {
            return this.contract
        }
    },
    created() {
        this.fetchApiKey()
    },
    mounted() {
        this.fetchDiary()
    },
    methods: {

        fetchApiKey() {
            this.apikey = null
            if (this.contract) {
                this.restFetchParams(this.contractUrl + '/diary/apikey', {contract: this.contract.id}, this.handleApiKey)
            }
        },

        handleApiKey(response) {
            this.apikey = response && response.status === 200
        },

        fetchDiary() {
            this.loading = true
            this.data = null
            // Must add some hours due to timezones
            const dateParam = new Date(this.date)
            dateParam.setHours(12)
            let params = {
                contract: this.contract.id,
                from: dateParam.toISOString()
            }
            this.restFetchParams(this.contractDiaryCityUrl, params, this.handleResult, this.handleError)
        },
        handleResult(response) {
            this.loading = false
            if (response && response.data) {
                this.data = response.data
            }
        },
        handleError() {
            this.loading = false
            this.showAlert(this.$t('city_contract_diary.failed_to_fetch_diary'))
        },
        getPhotoUrl: function (baseUrl, diary, photo) {
            return baseUrl + '/' + diary + '/' + photo
        },
        async downloadPdf() {
            this.loading = true
            const pdfBytes = await this.generatePdf()

            let dateFormat = ''
            if (this.diaryData) {
                const pdfDate = new Date(this.diaryData[this.dateField])
                dateFormat = `${pdfDate.getFullYear()}${String(pdfDate.getMonth() + 1).padStart(2, '0')}${String(pdfDate.getDate()).padStart(2, '0')}`
            }
            PdfGeneratorManager.downloadPdf(pdfBytes, `${this.$t(this.titleKey)} ${dateFormat}`.replaceAll(' ', '_'))
            this.loading = false
        },
        async generatePdf() {
            const {
                pdfDoc, pdfService, fonts, colors, footerLogo, companyFooterLogo, pdfGeneratorManager
            } = await this.initializePdf()

            const {fontText, fontHeader, fontBold} = fonts
            const {headerColor, textColor, boldColor} = colors

            let nextYPosition = undefined

            // Document title
            nextYPosition = pdfService.addText(pdfDoc, this.$t(this.titleKey), fontHeader, 20, nextYPosition, undefined, undefined, undefined, footerLogo, companyFooterLogo, headerColor)
            nextYPosition = pdfService.addText(pdfDoc, this.mode === 'weekly' ? this.getDate(this.diaryData.record_date, true) + ' - ' + this.getDate(this.diaryData.record_end_date, true) : this.getDate(this.diaryData[this.dateField], true), fontText, 12, nextYPosition, undefined, undefined, undefined, footerLogo, companyFooterLogo)
            nextYPosition = pdfService.addText(pdfDoc, `${this.diaryData.contract_name}`, fontText, 10, nextYPosition, undefined, undefined, undefined, footerLogo, companyFooterLogo)
            nextYPosition = pdfService.addText(pdfDoc, `${this.diaryData.contractor}`, fontText, 10, nextYPosition, undefined, undefined, undefined, footerLogo, companyFooterLogo)
            nextYPosition -= 20
            // Supervisors
            nextYPosition = pdfService.addText(pdfDoc, `${this.$t('contract_diary.supervisors')}`, fontBold, 14, nextYPosition, undefined, undefined, undefined, footerLogo, companyFooterLogo, boldColor)
            nextYPosition = pdfService.addText(pdfDoc, `${this.diaryData.supervisors}`, fontText, 12, nextYPosition, undefined, undefined, undefined, footerLogo, companyFooterLogo)
            nextYPosition -= 20
            // Events
            nextYPosition = pdfService.addText(pdfDoc, `${this.$t('contract_diary.events')}`, fontBold, 14, nextYPosition, undefined, undefined, undefined, footerLogo, companyFooterLogo, boldColor)
            nextYPosition = pdfService.addText(pdfDoc, `${this.diaryData.events}`, fontText, 12, nextYPosition, undefined, undefined, undefined, footerLogo, companyFooterLogo)
            nextYPosition -= 20
            // Deviations
            nextYPosition = pdfService.addText(pdfDoc, `${this.$t('contract_diary.deviations')}`, fontBold, 14, nextYPosition, undefined, undefined, undefined, footerLogo, undefined, boldColor)
            nextYPosition = pdfService.addText(pdfDoc, `${this.diaryData.deviations}`, fontText, 12, nextYPosition, undefined, undefined, undefined, footerLogo, companyFooterLogo)
            nextYPosition -= 20

            //Fetch photos
            const photos = await this.fetchPhotos()
            const images = []
            for (let photo of photos) {
                let image
                if (this.isPng(photo)) {
                    image = await pdfDoc.embedPng(photo)
                } else {
                    image = await pdfDoc.embedJpg(photo)
                }
                images.push(image)
            }

            //Add photos
            if (images.length > 1) {
                nextYPosition = pdfService.addText(pdfDoc, `${this.$t('ins_report_details.photos')}`, fontBold, 14, nextYPosition, undefined, undefined, undefined, footerLogo, undefined, boldColor)
                nextYPosition = pdfService.addImages(pdfDoc, undefined, images, undefined, nextYPosition, footerLogo, 1, 1, 3, 3, 1, companyFooterLogo)
                nextYPosition -= 55
            } else if (images.length >= 1) {
                nextYPosition = pdfService.addText(pdfDoc, `${this.$t('ins_report_details.photos')}`, fontBold, 14, nextYPosition, undefined, undefined, undefined, footerLogo, undefined, boldColor)
                nextYPosition = pdfService.addImage(pdfDoc, undefined, images[0], undefined, nextYPosition, footerLogo, 140, 1, 1, 1, companyFooterLogo)
                nextYPosition -= 55
            }

            // Tasks section
            nextYPosition = pdfService.addText(pdfDoc, this.$t('city_contract_diary.compelted_tasks'), fontBold, 12, nextYPosition, undefined, undefined, undefined, footerLogo, companyFooterLogo, boldColor)
            const tasksData = [
                [], // Header
                ...this.diaryData.tasks.map(item => [ // Data
                    item.task,
                    `${this.toLocalTime(item.start_time)} - ${this.toLocalTime(item.end_time)}`,
                    `${this.getWorkTime(Math.round(item.work_duration / 60))} ${this.$t('contracts.unit_hours')}`,
                    `${Number(item.work_distance).toFixed(2)} ${this.$t('contracts.unit_km')}`,
                ])
            ]
            nextYPosition = pdfService.addTable(tasksData, pdfDoc, {
                textFont: fontText, headerFont: fontBold, textFontSize: 9, headerFontSize: 9, textColor: textColor,
                headerFontColor: boldColor
            }, nextYPosition, undefined, undefined, footerLogo)
            // Substracting will move the text down
            nextYPosition -= 40

            // Resources section
            nextYPosition = pdfService.addText(pdfDoc, this.$t('city_contract_diary.resources'), fontBold, 12, nextYPosition, undefined, undefined, undefined, footerLogo, companyFooterLogo, boldColor)
            const resourcesData = this.diaryData.resources.map(item => [
                `${item.resource === -1 ? this.$t('city_contract_diary.resources_persons') : this.getVehicleTypeName(item.resource)}`,
                `${item.count} ${this.$t('common.pcs')}`,
            ])
            if (resourcesData.length === 0) {
                nextYPosition = pdfService.addText(pdfDoc, `-`, fontText, 12, nextYPosition, undefined, undefined, 25, footerLogo)
            } else {
                nextYPosition = pdfService.addVerticalTable(resourcesData, pdfDoc, {
                    textFont: fontText,
                    headerFont: fontBold,
                    textFontSize: 9,
                    headerFontSize: 9,
                    textColor: textColor,
                    headerColor: boldColor
                }, nextYPosition, undefined, undefined, footerLogo)
            }

            // Substracting will move the text down
            nextYPosition -= 40

            // Material section
            nextYPosition = pdfService.addText(pdfDoc, this.$t('city_contract_diary.materials'), fontBold, 12, nextYPosition, undefined, undefined, undefined, footerLogo, companyFooterLogo, boldColor)
            const materialsData = this.diaryData.materials.map(item => [
                `${item.material}`,
                `${item.amount} ${item.unit}`,
            ])
            if (materialsData.length === 0) {
                nextYPosition = pdfService.addText(pdfDoc, `-`, fontText, 12, nextYPosition, undefined, undefined, 25, footerLogo)
            } else {
                nextYPosition = pdfService.addVerticalTable(materialsData, pdfDoc, {
                    textFont: fontText,
                    headerFont: fontBold,
                    textFontSize: 9,
                    headerFontSize: 9,
                    textColor: textColor,
                    headerColor: boldColor
                }, nextYPosition, undefined, undefined, footerLogo)
            }
            // Substracting will move the text down
            nextYPosition -= 40

            // Danger reports section
            nextYPosition = pdfService.addText(pdfDoc, this.$t('city_contract_diary.danger_reports'), fontBold, 12, nextYPosition, undefined, undefined, undefined, footerLogo, companyFooterLogo, boldColor)
            const dangerReportsData = [
                [], // Header
                ...this.diaryData.danger_reports.map(item => [
                    `${item.incidence}`,
                    `${this.toLocalTime(item.time)}`,
                    `${item.description}`,
                ])
            ]
            if (dangerReportsData.length < 2) {
                nextYPosition = pdfService.addText(pdfDoc, `-`, fontText, 12, nextYPosition, undefined, undefined, 25, footerLogo)
            } else {
                nextYPosition = pdfService.addTable(dangerReportsData, pdfDoc, {
                    textFont: fontText,
                    headerFont: fontBold,
                    textFontSize: 9,
                    headerFontSize: 9,
                    textColor: textColor,
                    headerFontColor: boldColor
                }, nextYPosition, undefined, undefined, footerLogo)
            }
            // Substracting will move the text down
            nextYPosition -= 40

            // Weather section
            nextYPosition = pdfService.addText(pdfDoc, this.$t('weather_station.data_title'), fontBold, 12, nextYPosition, undefined, undefined, 100, footerLogo, companyFooterLogo, boldColor)
            nextYPosition -= 20
            const weatherData = [
                [ // Header
                    this.$t('contracts.external.time'),
                    this.$t('contracts.external.station'),
                    this.$t('contracts.external.air_temp'),
                    this.$t('contracts.external.road_temp'),
                    this.$t('contracts.external.avg_wind'),
                    this.$t('contracts.external.rain_type'),
                    this.$t('contracts.external.rain_amount')
                ],
                // Data
                ...this.diaryData.weather.map(item => {
                    const station = item.saatieto['aseman-tunniste']
                    return [
                        this.toLocalTime(item.saatieto.havaintoaika),
                        station ? station.replaceAll("_", " ") : "",
                        item.saatieto['ilman-lampotila'],
                        item.saatieto['tien-lampotila'],
                        item.saatieto['keskituuli'],
                        this.getRainTypeText(item.saatieto['sateen-olomuoto']),
                        item.saatieto['sadesumma']
                    ]
                })
            ]
            if (weatherData.length < 2) {
                nextYPosition = pdfService.addText(pdfDoc, `-`, fontText, 12, nextYPosition, undefined, undefined, 25, footerLogo, companyFooterLogo)
            } else {
                nextYPosition = pdfService.addTable(weatherData, pdfDoc, {
                    textFont: fontText,
                    headerFont: fontBold,
                    textFontSize: 9,
                    headerFontSize: 9,
                    textColor: textColor,
                    headerFontColor: boldColor
                }, nextYPosition, undefined, undefined, footerLogo)
            }

            return await pdfGeneratorManager.generateBytesFromPdfDoc(pdfDoc)
        },
        isPng(photo) {
            return photo.startsWith('data:image/png;');
        },
        async fetchPhotos() {
            const results = []
            for (const photo of this.diaryData.photos) {
                let url = this.getPhotoUrl(
                    this.mode === 'weekly' ? this.contractWeeklyDiaryPhotoUrl : this.contractDiaryPhotoUrl,
                    this.mode === 'weekly' ? this.diaryData.id : photo.diary,
                    this.mode === 'weekly' ? photo.id : photo.photo
                )
                results.push((await new Promise(resolve => this.restFetch(url, resolve))).data)
            }
            return results
        },
        async sendToBuildie() {
            try {
                this.loading = true
                const pdfBytes = await this.generatePdf()
                const pdfBlob = new Blob([pdfBytes], {type: 'application/pdf'})
                const formData = new FormData()
                let dateFormat = ''
                if (this.diaryData) {
                    const pdfDate = new Date(this.diaryData[this.dateField])
                    dateFormat = `${pdfDate.getFullYear()}${String(pdfDate.getMonth() + 1).padStart(2, '0')}${String(pdfDate.getDate()).padStart(2, '0')}`
                }
                const fileName = `${this.$t(this.titleKey)} ${dateFormat}.pdf`.replaceAll(' ', '_')
                formData.append('file', pdfBlob, fileName)
                formData.append('date', this.getDateIsoString(this.date))
                formData.append('contract', this.contract.id)

                this.restPostWithFile(this.extBuildieUrl, {}, formData, () => {
                    this.loading = false
                    this.showAlert(this.$t('city_contract_diary.success_to_send_diary_to_integration'), 'success')
                    this.$emit('update')
                }, () => {
                    this.loading = false
                    this.showAlert(this.$t('city_contract_diary.failed_to_send_diary_to_integration'))
                })
            } catch (error) {
                this.loading = false
                this.showAlert(this.$t('city_contract_diary.failed_to_send_diary_to_integration'))
            }
        }
    }
}
</script>
<style scoped>
.diary {
    border: 1px #bcbcbc solid;
    border-radius: .5em;
}

.space-cells td {
    width: 50%;
}

.image-container {
    width: fit-content;
}
</style>
